import { ReactElement, useEffect } from 'react';

import { useOidc } from '@changegps/access-identity';
import { AppLoader, LandingPage } from '@changegps/horizon-component-library';
import { Stack } from '@mui/material';
import {
  MutationKey,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import { getUser } from '@/features/users/api';
import { usersQueryKeys } from '@/features/users/data';

const base: MutationKey = ['login', 'oidc', 'callback'];

export const loginMutationKeys = {
  // * BASE
  baseKey: base,

  // * Send Login
  sendLogin: [...base, 'sendLogin'],
};

export const oidcCallbackMutationKeys = {
  baseKey: base,
  handleCallback: [...base, 'handleCallback'] as const,
} as const;

export type OIDCCallbackMutationKey =
  typeof oidcCallbackMutationKeys.handleCallback;

export const OIDCCallback = (): ReactElement => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { handleCallback } = useOidc();

  const { mutate } = useMutation({
    mutationKey: oidcCallbackMutationKeys.handleCallback,
    mutationFn: handleCallback,
    onSuccess: async () => {
      // Get user info and set it in the query client
      const userInfo = await getUser();
      if (userInfo) {
        queryClient.setQueryData(usersQueryKeys.getUserInfo, userInfo);
        navigate('/home');
      } else {
        navigate('/login?error=invalid_credentials');
      }
    },
    onError: () => {
      navigate('/login?error=invalid_credentials');
    },
  });

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get('code');
    const state = searchParams.get('state');

    if (code && state) {
      mutate();
    } else {
      navigate('/login?error=invalid_credentials');
    }
  }, [location.search, mutate, navigate]);

  return (
    <LandingPage>
      <Stack
        spacing={2}
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
      >
        <AppLoader />
      </Stack>
    </LandingPage>
  );
};
